// PrepAnnaChat — Chat with Anna in Written Preparation
// Hybrid Socratic approach: Anna asks first, gives text when understanding is shown.
// Parses <note_update> tags and shows accept/customize/dismiss cards.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { PrepChatService, PrepChatMessage, NoteUpdateSuggestion, stripNoteUpdateTags, stripConceptQualifiedTags } from '../../services/prepChat';
import Button from '../ui/Button';
import AnnaGlow from '../ui/AnnaGlow';

interface PrepAnnaChatProps {
  topicTitle: string;
  subtopics: Array<{ id: string; title: string }>;
  currentNotes: string;
  onNoteUpdate: (content: string, summary: string) => void;
  onClose?: () => void;
  examConcept?: { id: string; term: string; description: string } | null;
  onConceptQualified?: (conceptId: string) => void;
}

const getFirstName = (displayName: string | null | undefined): string => {
  if (!displayName) return '';
  return displayName.split(' ')[0] || '';
};

// Helper to strip all structured tags from display text
const getDisplayText = (msg: PrepChatMessage): string => {
  let text = msg.content;
  if (msg.noteUpdate) text = stripNoteUpdateTags(text);
  if (msg.conceptQualified) text = stripConceptQualifiedTags(text);
  return text;
};

const PrepAnnaChat: React.FC<PrepAnnaChatProps> = ({
  topicTitle,
  subtopics,
  currentNotes,
  onNoteUpdate,
  onClose,
  examConcept,
  onConceptQualified,
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('preparation');

  const [service] = useState(() => new PrepChatService({
    topicTitle,
    subtopics,
    language: i18n.language,
  }));

  const [messages, setMessages] = useState<PrepChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track which note updates are being customized (by message id)
  const [customizing, setCustomizing] = useState<Record<string, string>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const examConceptIdRef = useRef<string | null>(null);

  // Initialize with welcome message
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const firstName = getFirstName(user?.displayName);
    const welcome: PrepChatMessage = {
      id: `welcome-${Date.now()}`,
      role: 'assistant',
      content: t('chat.welcome', { firstName, topicTitle }),
      timestamp: new Date(),
    };
    setMessages([welcome]);
    inputRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll on new messages
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [messages]);

  // Handle exam concept qualification mode
  useEffect(() => {
    if (examConcept && examConcept.id !== examConceptIdRef.current) {
      examConceptIdRef.current = examConcept.id;
      service.setExamConcept({ term: examConcept.term, description: examConcept.description });

      // Auto-send qualification request
      const qualifyPrompt = t('chat.qualifyConceptPrompt', { term: examConcept.term });
      const userMsg: PrepChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: qualifyPrompt,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);
      setIsLoading(true);
      setError(null);

      (async () => {
        try {
          const response = await service.sendMessage(qualifyPrompt, currentNotes);
          setMessages(prev => [...prev, response]);
        } catch (err) {
          setError(err instanceof Error ? err.message : t('chat.errorUnknown'));
        } finally {
          setIsLoading(false);
          inputRef.current?.focus();
        }
      })();
    } else if (!examConcept && examConceptIdRef.current) {
      examConceptIdRef.current = null;
      service.setExamConcept(null);
    }
  }, [examConcept]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isLoading) return;

    const userMsg: PrepChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await service.sendMessage(text, currentNotes);
      setMessages(prev => [...prev, response]);

      // Auto-qualify concept if Anna marked it
      if (response.conceptQualified && examConcept && onConceptQualified) {
        onConceptQualified(examConcept.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat.errorUnknown'));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [inputText, isLoading, service, currentNotes, t, examConcept, onConceptQualified]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInsert = (update: NoteUpdateSuggestion) => {
    onNoteUpdate(update.content, update.changeSummary);
  };

  const handleCustomize = (msgId: string, update: NoteUpdateSuggestion) => {
    setCustomizing(prev => ({ ...prev, [msgId]: update.content }));
  };

  const handleCustomizeConfirm = (msgId: string, summary: string) => {
    const content = customizing[msgId];
    if (content) {
      onNoteUpdate(content, summary);
      setCustomizing(prev => {
        const next = { ...prev };
        delete next[msgId];
        return next;
      });
    }
  };

  const handleDismiss = (msgId: string) => {
    // Remove from customizing if it was being customized
    setCustomizing(prev => {
      const next = { ...prev };
      delete next[msgId];
      return next;
    });
  };

  const formatTime = (date: Date) =>
    new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary-500 dark:bg-primary-700 text-white">
        <div className="flex items-center gap-2">
          <AnnaGlow size="sm" />
          <div>
            <h3 className="font-semibold text-sm">{t('chat.headerTitle')}</h3>
            <p className="text-xs text-primary-100 truncate">{topicTitle}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={t('chat.close')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id}>
            {/* Message bubble */}
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary-500 text-white rounded-br-md'
                    : 'bg-gray-100 dark:bg-gray-800 text-neutral-dark dark:text-gray-200 rounded-bl-md'
                }`}
              >
                <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1">
                  <ReactMarkdown>
                    {getDisplayText(msg)}
                  </ReactMarkdown>
                </div>
                <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-primary-200' : 'text-gray-400 dark:text-gray-500'}`}>
                  {formatTime(msg.timestamp)}
                </div>
              </div>
            </div>

            {/* Concept qualified success card */}
            {msg.conceptQualified && (
              <div className="mt-2 ml-0 max-w-[85%]">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">
                      {t('chat.conceptQualified.title')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Note update suggestion card */}
            {msg.noteUpdate && (
              <div className="mt-2 ml-0 max-w-[85%]">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm">📝</span>
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      {t('chat.noteUpdate.suggestion')}
                    </span>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                    {msg.noteUpdate.changeSummary}
                  </p>

                  {customizing[msg.id] !== undefined ? (
                    /* Customize mode */
                    <div className="space-y-2">
                      <textarea
                        value={customizing[msg.id]}
                        onChange={(e) => setCustomizing(prev => ({ ...prev, [msg.id]: e.target.value }))}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded resize-none focus:outline-none focus:ring-1 focus:ring-amber-500 min-h-[80px] text-neutral-dark dark:text-gray-200"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCustomizeConfirm(msg.id, msg.noteUpdate!.changeSummary)}
                          className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                        >
                          {t('chat.noteUpdate.insert')}
                        </button>
                        <button
                          onClick={() => handleDismiss(msg.id)}
                          className="text-xs px-3 py-1.5 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors"
                        >
                          {t('chat.noteUpdate.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Action buttons */
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleInsert(msg.noteUpdate!)}
                        className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                      >
                        {t('chat.noteUpdate.insert')}
                      </button>
                      <button
                        onClick={() => handleCustomize(msg.id, msg.noteUpdate!)}
                        className="text-xs px-3 py-1.5 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                      >
                        {t('chat.noteUpdate.customize')}
                      </button>
                      <button
                        onClick={() => handleDismiss(msg.id)}
                        className="text-xs px-3 py-1.5 text-amber-600 dark:text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors"
                      >
                        {t('chat.noteUpdate.dismiss')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-gray-500">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm">{t('chat.annaTyping')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex justify-center">
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-2 text-sm">
              {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setInputText(t('chat.quickAction.helpFormulatePrompt'))}
            className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
            disabled={isLoading}
          >
            ✍️ {t('chat.quickAction.helpFormulate')}
          </button>
          <button
            onClick={() => setInputText(t('chat.quickAction.checkNotesPrompt'))}
            className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
            disabled={isLoading}
          >
            🔍 {t('chat.quickAction.checkNotes')}
          </button>
          <button
            onClick={() => setInputText(t('chat.quickAction.suggestStructurePrompt'))}
            className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
            disabled={isLoading}
          >
            📋 {t('chat.quickAction.suggestStructure')}
          </button>
        </div>
      </div>

      {/* Input */}
      <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.inputPlaceholder')}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-neutral-dark dark:text-gray-200 placeholder:text-gray-400"
            disabled={isLoading}
          />
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!inputText.trim() || isLoading}
            className="rounded-full px-4"
            aria-label={t('chat.send')}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrepAnnaChat;
